import { createBrowserRouter, RouterProvider, Navigate } from 'react-router-dom';
import type { ReactNode } from 'react';
import { AdminLayout } from './components/layout/AdminLayout';
import { ProtectedRoute, RequireAdminPermission } from './components/layout/ProtectedRoute';
import { LoginPage } from './routes/login';
import { HomePage } from './routes/home';
import { InvitationPolicyPage } from './routes/settings/invitation-policy';
import { InvitationRulesPage } from './routes/settings/invitation-rules';
import { CaptchaSettingsPage } from './routes/settings/captcha';
import { OCRSettingsPage } from './routes/settings/ocr';
import { VerificationEmailPage } from './routes/settings/verification-email';
import { CommissionPolicyPage } from './routes/settings/commission-policy';
import { NewcomerRewardPage } from './routes/settings/newcomer-reward';
import { MarketsPage } from './routes/settings/markets';
import { WithdrawalPolicyPage } from './routes/settings/withdrawal-policy';
import { TaskRiskPolicyPage } from './routes/settings/task-risk-policy';
import { WithdrawalRiskPolicyPage } from './routes/settings/withdrawal-risk-policy';
import { WithdrawalPayoutPage } from './routes/settings/withdrawal-payout';
import { MediaStoragePage } from './routes/settings/media-storage';
import { WithdrawalRedPacketPage } from './routes/settings/withdrawal-red-packet';
import { HomeContentPage } from './routes/settings/home-content';
import { CustomerServicePage } from './routes/settings/customer-service';
import { FAQPage } from './routes/settings/faq';
import { AppUpdatePage } from './routes/settings/app-update';
import { AppleAuthPage } from './routes/settings/apple-auth';
import { MessageReviewPage } from './routes/settings/message-review';
import { LegalDocumentsPage } from './routes/legal-documents';
import { TasksPage } from './routes/tasks';
import { TaskCreatePage } from './routes/task-create';
import { TaskDetailPage } from './routes/task-detail';
import { AssignmentsPage } from './routes/assignments';
import { AssignmentDetailPage } from './routes/assignment-detail';
import { WithdrawalsPage } from './routes/withdrawals';
import { WithdrawalStatisticsPage } from './routes/withdrawal-statistics';
import { WithdrawalTargetRiskPage } from './routes/withdrawal-target-risk';
import { WithdrawalAccountsPage } from './routes/withdrawal-accounts';
import { DownloadOnlyTaskUsersPage } from './routes/download-only-task-users';
import { InvitationCodesPage } from './routes/invitation-codes';
import { CommissionsPage } from './routes/commissions';
import { RewardsPage } from './routes/rewards';
import { TeamPage } from './routes/team';
import { NotificationsPage } from './routes/notifications';
import { SupportFeedbackPage } from './routes/support-feedback';
import { UsersPage } from './routes/users';
import { UserDetailPage } from './routes/user-detail';
import { SystemLogsPage } from './routes/system-logs';
import { AdminAccountsPage } from './routes/admin-accounts';
import { AgentTaskSettingsPage } from './routes/agent-task-settings';
import { AgentFinancePage } from './routes/agent-finance';
import { AgentsPage, AgentDetailPage, AgentAuditPage } from './routes/agents';
import { AgentTasksPage, AgentTaskDetailPage } from './routes/agent-tasks';
import { AgentReportsPage } from './routes/agent-reports';
import { AgentFeedbackPage, AgentFeedbackDetailPage } from './routes/agent-feedback';
import { AdvertisingPage, AdvertisingDetailPage, AdvertisingSlotsPage } from './routes/advertising';
import { AdvertisingReportsPage } from './routes/advertising-reports';
import type { AdminPermissionKey } from './lib/adminPermissions';

function requirePermission(permission: AdminPermissionKey, element: ReactNode) {
  return <RequireAdminPermission permission={permission}>{element}</RequireAdminPermission>;
}

const router = createBrowserRouter([
  {
    path: '/login',
    element: <LoginPage />,
  },
  {
    element: <ProtectedRoute />,
    children: [
      {
        element: <AdminLayout />,
        children: [
          { index: true, element: requirePermission('dashboard', <HomePage />) },
          { path: 'settings/invitation-policy', element: requirePermission('settings', <InvitationPolicyPage />) },
          { path: 'settings/invitation-rules', element: requirePermission('settings', <InvitationRulesPage />) },
          { path: 'settings/captcha', element: requirePermission('settings', <CaptchaSettingsPage />) },
          { path: 'settings/ocr', element: requirePermission('settings', <OCRSettingsPage />) },
          { path: 'settings/verification-email', element: requirePermission('settings', <VerificationEmailPage />) },
          { path: 'settings/commission-policy', element: requirePermission('settings', <CommissionPolicyPage />) },
          { path: 'settings/newcomer-reward', element: requirePermission('settings', <NewcomerRewardPage />) },
          { path: 'settings/markets', element: requirePermission('settings', <MarketsPage />) },
          { path: 'settings/withdrawal-policy', element: requirePermission('settings', <WithdrawalPolicyPage />) },
          { path: 'settings/task-risk-policy', element: requirePermission('settings', <TaskRiskPolicyPage />) },
          { path: 'settings/withdrawal-risk-policy', element: requirePermission('settings', <WithdrawalRiskPolicyPage />) },
          { path: 'settings/withdrawal-payout', element: requirePermission('settings', <WithdrawalPayoutPage />) },
          { path: 'settings/media-storage', element: requirePermission('settings', <MediaStoragePage />) },
          { path: 'settings/withdrawal-red-packet', element: requirePermission('settings', <WithdrawalRedPacketPage />) },
          { path: 'settings/home-content', element: requirePermission('settings', <HomeContentPage />) },
          { path: 'settings/customer-service', element: requirePermission('settings', <CustomerServicePage />) },
          { path: 'settings/faq', element: requirePermission('settings', <FAQPage />) },
          { path: 'settings/app-update', element: requirePermission('settings', <AppUpdatePage />) },
          { path: 'settings/apple-auth', element: requirePermission('settings', <AppleAuthPage />) },
          { path: 'settings/message-review', element: requirePermission('settings', <MessageReviewPage />) },
          { path: 'settings/legal-documents', element: requirePermission('settings', <LegalDocumentsPage />) },
          { path: 'agent-task-settings', element: requirePermission('agents.view', <AgentTaskSettingsPage />) },
          { path: 'agent-finance', element: requirePermission('agent_finance.view', <AgentFinancePage />) },
          { path: 'agents', element: requirePermission('agents.view', <AgentsPage />) },
          { path: 'agents/:id', element: requirePermission('agents.view', <AgentDetailPage />) },
          { path: 'agent-tasks', element: requirePermission('agent_tasks.view', <AgentTasksPage />) },
          { path: 'agent-tasks/:id', element: requirePermission('agent_tasks.view', <AgentTaskDetailPage />) },
          { path: 'agent-task-reviews', element: requirePermission('agent_tasks.review', <AgentTasksPage review />) },
          { path: 'agent-task-reviews/:id', element: requirePermission('agent_tasks.review', <AgentTaskDetailPage />) },
          { path: 'agent-reports', element: requirePermission('agent_reports.view', <AgentReportsPage />) },
          { path: 'agent-feedback', element: requirePermission('agent_tasks.view', <AgentFeedbackPage />) },
          { path: 'agent-feedback/:id', element: requirePermission('agent_tasks.view', <AgentFeedbackDetailPage />) },
          { path: 'agent-audit', element: requirePermission('agent_logs.view', <AgentAuditPage />) },
          { path: 'advertising', element: requirePermission('advertising.view', <AdvertisingPage />) },
          { path: 'advertising/new', element: requirePermission('advertising.manage', <AdvertisingDetailPage />) },
          { path: 'advertising/reviews', element: requirePermission('advertising.review', <AdvertisingPage review />) },
          { path: 'advertising/slots', element: requirePermission('advertising.view', <AdvertisingSlotsPage />) },
          { path: 'advertising/reports', element: requirePermission('advertising.reports.view', <AdvertisingReportsPage />) },
          { path: 'advertising/:id', element: requirePermission('advertising.view', <AdvertisingDetailPage />) },
          { path: 'admin-accounts', element: requirePermission('admin_accounts', <AdminAccountsPage />) },
          { path: 'tasks', element: requirePermission('tasks', <TasksPage />) },
          { path: 'tasks/create', element: requirePermission('tasks', <TaskCreatePage />) },
          { path: 'tasks/:id', element: requirePermission('tasks', <TaskDetailPage />) },
          { path: 'assignments', element: requirePermission('assignments.view', <AssignmentsPage />) },
          { path: 'assignments/:id', element: requirePermission('assignments.view', <AssignmentDetailPage />) },
          { path: 'withdrawals', element: requirePermission('withdrawals.view', <WithdrawalsPage />) },
          { path: 'withdrawal-statistics', element: requirePermission('withdrawals', <WithdrawalStatisticsPage />) },
          { path: 'withdrawal-target-risk', element: requirePermission('withdrawals', <WithdrawalTargetRiskPage />) },
          { path: 'withdrawal-accounts', element: requirePermission('withdrawals', <WithdrawalAccountsPage />) },
          { path: 'download-only-task-users', element: requirePermission('withdrawals', <DownloadOnlyTaskUsersPage />) },
          { path: 'notifications', element: requirePermission('notifications', <NotificationsPage />) },
          { path: 'support-feedback', element: requirePermission('support_feedback', <SupportFeedbackPage />) },
          { path: 'invitation/codes', element: requirePermission('invitation', <InvitationCodesPage />) },
          { path: 'rewards', element: requirePermission('rewards', <RewardsPage />) },
          { path: 'commissions', element: requirePermission('commissions', <CommissionsPage />) },
          { path: 'users', element: requirePermission('users', <UsersPage />) },
          { path: 'users/:id', element: requirePermission('users', <UserDetailPage />) },
          { path: 'team', element: requirePermission('team', <TeamPage />) },
          { path: 'system-logs', element: requirePermission('system_logs', <SystemLogsPage />) },
        ],
      },
    ],
  },
  {
    path: '*',
    element: <Navigate to="/" replace />,
  },
]);

export function App() {
  return <RouterProvider router={router} />;
}
